// WebSocket Manager for orchestration agent
import { ensureResultField } from './responseTransformers';
import { setStoredOrchestrationSessionId, getStoredOrchestrationSessionId } from './orchestrationSession';
import { resetOrchestrationSocketJwtBinding } from '../../utils/orchestrationPayload';
import { devError } from '../../utils/safeClientLog';
import {
  getJwtTokenCookie,
  getWsAuthToken,
  handleExpiredAuthSession,
  hasClientAuthSession,
  shouldUseServerSessionAuth,
} from '../../utils/authCookie';
import { getReactEnvValue, isReactStrictEnv } from '../../utils/envConfig';
import {
  getHeldConversations,
  updateHeldConversations,
  setHeldShowResults,
} from '../../utils/AIPrescription/conversationUtils';
import {
  createPrescriptionsFromMedicationData,
  isValidMedicationItem,
} from '../../utils/AIPrescription/prescriptionUtils';
import type { ConversationItem } from '../../utils/AIPrescription/types';
import { getPatientId } from '../../utils/patientSession';

interface ResumedConversationHistoryEntry {
  role: string;
  content: string;
  timestamp?: string;
  // Present on assistant entries written after the per-turn snapshot was added to
  // conversation_history (services/session_intent_reducer.py) -- absent on older entries
  // from before that change, or on the "user" role entries, which never carry a draft.
  medication_data?: any[] | null;
  current_state?: string | null;
  drug_interaction_response?: any | null;
  drug_interaction_response_raw?: any | null;
  // Option-B resume architecture: allergy/favourite-drugs/selection-chip data attached to
  // the SAME per-turn entry as everything above, so each turn's own value is preserved
  // independently instead of a single session-wide field a later turn could overwrite.
  allergy_response?: any | null;
  favourite_drugs_data?: any[] | null;
  favourite_drugs?: (string | any)[] | null;
  suggestion_groups?: { medication_name: string; options: string[] }[] | null;
  suggestions?: (string | any)[] | null;
  // 2026-09 refresh-architecture audit (Category A): the remaining live-WebSocket-path
  // fields that never reached the resume snapshot -- pharmacy/PBM pickers, the "sent to
  // pharmacy" badge, the feedback trace id, and in-chat tab-nav links. Same per-turn
  // pattern as everything above.
  pharmacy_suggestions_data?: any[] | null;
  pharmacy_suggestion_groups?: { medication_name: string; options: any[]; fallback_mode?: string | null }[] | null;
  pharmacy_search_fallback_mode?: string | null;
  pbm_suggestions_data?: any[] | null;
  sent_medication_data?: any[] | null;
  trace_id?: string | null;
  app_tab_link?: { path: string; label: string } | null;
  app_tab_links?: { path: string; label: string }[] | null;
}

interface ResumedConversationSnapshot {
  conversation_history: ResumedConversationHistoryEntry[];
  // Session-level "current" snapshot -- used as a fallback for the LAST item only, when its
  // own history entry predates the per-turn snapshot (backward compatibility for sessions
  // that were already open when this shipped).
  medication_data?: any[] | null;
  current_state?: string | null;
  prescription_cards?: any[] | null;
  ui_blocks?: string[] | null;
  drug_interaction_response?: any | null;
  patient_uid?: string | null;
  allergy_response?: any | null;
  favourite_drugs_data?: any[] | null;
  favourite_drugs?: (string | any)[] | null;
  suggestion_groups?: { medication_name: string; options: string[] }[] | null;
  suggestions?: (string | any)[] | null;
}

/** Builds the card fields for one item from a medication_data snapshot. */
function buildPrescriptionFields(
  medicationData: any[] | null | undefined,
  aiResponse: string
): { structuredMedicationData: any[]; prescriptions: any[]; originalPrescription: any } {
  const validMedicationData = (medicationData || []).filter(isValidMedicationItem);
  const prescriptions = createPrescriptionsFromMedicationData(validMedicationData, {
    aiResponse,
  });
  return {
    structuredMedicationData: validMedicationData,
    prescriptions,
    originalPrescription: prescriptions[0],
  };
}

/**
 * Rebuilds chat bubbles from the backend's retained per-turn history on a resumed connection
 * (e.g. after a page refresh). Each turn's OWN medication_data/current_state (stored alongside
 * its response text since the history-snapshot change) rebuilds that turn's own summary card —
 * not just the most recent one, since the session's live medication_data gets overwritten by
 * later turns but each history entry's copy does not. Entries from before that change (no
 * medication_data key at all) fall back to the session-level "current" snapshot, but only for
 * the LAST item, matching the previous (latest-turn-only) behavior for old sessions.
 */
export function applyResumedConversation(snapshot: ResumedConversationSnapshot): void {
  const history = Array.isArray(snapshot.conversation_history) ? snapshot.conversation_history : [];
  if (history.length === 0) return;

  const items: ConversationItem[] = [];
  let id = 0;
  for (let i = 0; i < history.length; i++) {
    const entry = history[i];
    if (entry?.role !== 'user') continue;
    const next = history[i + 1];
    const hasReply = next?.role === 'assistant';
    const aiResponse = hasReply ? next.content || '' : '';
    const item: ConversationItem = {
      id: id++,
      userQuery: entry.content || '',
      aiResponse,
      isLoading: false,
      prescriptions: [],
      timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
    };
    if (hasReply && next.medication_data !== undefined) {
      const fields = buildPrescriptionFields(next.medication_data, aiResponse);
      item.structuredMedicationData = fields.structuredMedicationData;
      item.prescriptions = fields.prescriptions;
      item.originalPrescription = fields.originalPrescription;
      item.currentState = next.current_state || undefined;

      // Apply drug interaction response from the TURN it came from, not from global snapshot.
      // Each turn carries its own DI data so sequential medications don't overwrite each other.
      if (next.drug_interaction_response) {
        item.drugInteractionResponse = next.drug_interaction_response;
      }
    }
    if (hasReply) {
      // Same per-turn principle as drug_interaction_response above (Option-B resume
      // architecture): allergy/favourite-drugs/selection-chip data is read from THIS turn's
      // own history entry, not gated on medication_data, so a non-medication turn (e.g. an
      // allergy lookup with no active draft) still reconstructs correctly.
      if (next.allergy_response) {
        item.allergyResponse = next.allergy_response;
      }
      if (next.favourite_drugs_data) {
        item.favouriteDrugs = next.favourite_drugs_data;
      } else if (next.favourite_drugs) {
        item.favouriteDrugs = next.favourite_drugs;
      }
      if (next.suggestion_groups) {
        item.suggestionGroups = next.suggestion_groups;
      }
      if (next.suggestions) {
        item.suggestions = next.suggestions;
      }
      // Same per-turn principle: pharmacy/PBM picker rows, the "sent" badge, the feedback
      // trace id, and tab-nav links are read from THIS turn's own history entry so the
      // resume path reconstructs the identical ConversationItem shape the live WebSocket
      // path builds (see useWebSocketMessageHandler.ts's pharmacyLookupRowsFromResultData /
      // pbmLookupRowsFromResultData / appTabLink(s)FromResultData for the live-path
      // equivalents these mirror).
      if (next.pharmacy_suggestions_data && next.pharmacy_suggestions_data.length > 0) {
        item.pharmacyLookup = next.pharmacy_suggestions_data;
      }
      if (next.pharmacy_suggestion_groups) {
        item.pharmacySuggestionGroups = next.pharmacy_suggestion_groups;
      }
      if (next.pharmacy_search_fallback_mode) {
        item.pharmacySearchFallbackMode = next.pharmacy_search_fallback_mode;
      }
      if (next.pbm_suggestions_data && next.pbm_suggestions_data.length > 0) {
        item.pbmLookup = next.pbm_suggestions_data;
      }
      if (next.sent_medication_data && next.sent_medication_data.length > 0) {
        const _names = Array.from(
          new Set(
            next.sent_medication_data
              .map((m: any) => (m && typeof m === 'object' ? String(m.medication_name || m.drugName || '').trim() : ''))
              .filter((n: string) => n.length > 0)
          )
        );
        if (_names.length > 0) item.sentMedicationNames = _names;
      }
      if (next.trace_id) {
        item.traceId = next.trace_id;
      }
      if (next.app_tab_links && next.app_tab_links.length > 0) {
        item.appTabLinks = next.app_tab_links;
        item.appTabLink = next.app_tab_links[0];
      } else if (next.app_tab_link) {
        item.appTabLink = next.app_tab_link;
        item.appTabLinks = [next.app_tab_link];
      }
    }
    items.push(item);
    if (hasReply) i++;
  }
  if (items.length === 0) return;

  const last = items[items.length - 1];
  if (last.structuredMedicationData === undefined) {
    // Fallback for a last turn recorded before the per-turn snapshot existed.
    const fields = buildPrescriptionFields(snapshot.medication_data, last.aiResponse);
    last.structuredMedicationData = fields.structuredMedicationData;
    last.prescriptions = fields.prescriptions;
    last.originalPrescription = fields.originalPrescription;
    last.currentState = snapshot.current_state || undefined;
  }

  // Apply UI control data (ui_blocks and drug interaction response) to the last item
  // so that interactions and other UI surfaces re-render correctly on page refresh.
  // Only apply snapshot DI to the last item if it doesn't already have DI from history.
  if (snapshot.ui_blocks) {
    last.uiBlocks = snapshot.ui_blocks;
  }
  if (snapshot.drug_interaction_response && !last.drugInteractionResponse) {
    last.drugInteractionResponse = snapshot.drug_interaction_response;
  }
  // Session-level fallback for the last item only, same pattern as DI above -- covers
  // entries recorded before allergy/favourite/suggestion per-turn storage existed.
  if (snapshot.allergy_response && !last.allergyResponse) {
    last.allergyResponse = snapshot.allergy_response;
  }
  if (!last.favouriteDrugs) {
    if (snapshot.favourite_drugs_data) {
      last.favouriteDrugs = snapshot.favourite_drugs_data;
    } else if (snapshot.favourite_drugs) {
      last.favouriteDrugs = snapshot.favourite_drugs;
    }
  }
  if (snapshot.suggestion_groups && !last.suggestionGroups) {
    last.suggestionGroups = snapshot.suggestion_groups;
  }
  if (snapshot.suggestions && !last.suggestions) {
    last.suggestions = snapshot.suggestions;
  }

  updateHeldConversations(() => items);
  setHeldShowResults(true);
}

function resolveOrchestrationWsUrl(): string {
  const explicit = getReactEnvValue("REACT_APP_ORCHESTRATION_WS_URL");
  if (explicit) {
    if (isReactStrictEnv() && explicit.startsWith("ws://")) {
      throw new Error("REACT_APP_ORCHESTRATION_WS_URL must use wss:// outside local/dev");
    }
    return explicit;
  }
  const hub = getReactEnvValue("REACT_APP_API_URL");
  if (hub) {
    const base = hub.replace(/\/+$/, "");
    if (base.endsWith("/ws")) {
      return base;
    }
    return `${base.replace(/^http/, (m) => (m === "https" ? "wss" : "ws"))}/ws`;
  }
  return "ws://localhost:5000/ws";
}

const TEXT_API_URL = resolveOrchestrationWsUrl();

interface PendingRequest {
  resolve: (value: any) => void;
  reject: (error: any) => void;
  timeout: NodeJS.Timeout;
  requestId: string;
  payload: any;
  retryOnIdleTimeout: boolean;
}

class TextWebSocketManager {
  private ws: WebSocket | null = null;
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  // After the fast burst is exhausted we keep a slow background retry instead of giving up,
  // so the socket self-heals after a backend restart / session expiry without a page refresh.
  private slowReconnectDelay: number = 30000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  // Coalesces concurrent connect() calls. connect() awaits getWsAuthToken() before assigning
  // this.ws, so without this two callers (eager mount + lazy send + reconnect) would each create
  // a socket; the first connects as an orphan ("connected" but unused) while this.ws points at the
  // second — the send then waits on the wrong/dead socket.
  private connectPromise: Promise<void> | null = null;
  private isManualClose: boolean = false;
  private requestIdCounter: number = 0;
  private readonly REQUEST_TIMEOUT: number = 30000; // 30 seconds
  private preferredPharmacyCallback: ((data: any) => void) | null = null;
  private intermediateMessageCallback: ((message: any) => void) | null = null;

  constructor() {
    this.connect();
  }

  /**
   * Register callback for pharmacy updates
   */
  public setPreferredPharmacyCallback(callback: (data: any) => void): void {
    this.preferredPharmacyCallback = callback;
  }

  /**
   * Register callback for intermediate WebSocket messages (status, token, etc.)
   */
  public setIntermediateMessageCallback(callback: (message: any) => void): void {
    this.intermediateMessageCallback = callback;
  }

  /**
   * Transform WebSocket response to match expected format
   * WebSocket returns: { type: "result", data: { medication_data: [...], response: "...", validation: {...} } }
   * Code expects: { data: { result: [...] } }
   */
  private transformResponse(response: any): any {
    if (!response || typeof response !== 'object') {
      return response;
    }

    // Handle result type responses specifically
    if (response.type === "result" && response.data) {
      const data = response.data;
      
      // Ensure medication_data is properly handled
      if (data.medication_data !== undefined) {
        // Ensure medication_data is an array
        const medicationData = Array.isArray(data.medication_data) 
          ? data.medication_data 
          : (data.medication_data ? [data.medication_data] : []);
        
        const transformedData = {
          ...data,
          medication_data: medicationData,
          result: medicationData // Map to result for compatibility
        };
        
        const transformed = {
          ...response,
          data: transformedData
        };
        
        return transformed;
      } else {
        // No medication_data, but still preserve the response structure
        const transformedData = {
          ...data,
          medication_data: [],
          result: [] // Set empty arrays
        };
        
        const transformed = {
          ...response,
          data: transformedData
        };
        
        return transformed;
      }
    }

    // Get the data object (either response.data or response itself)
    const data = response.data || response;
    
    // If data has medication_data, map it to result for compatibility
    if (data && typeof data === 'object' && data.medication_data) {
      const medicationData = Array.isArray(data.medication_data) 
        ? data.medication_data 
        : (data.medication_data ? [data.medication_data] : []);
      
      const transformedData = {
        ...data,
        medication_data: medicationData,
        result: medicationData
      };
      
      const transformed = {
        ...response,
        data: transformedData
      };
      
      return transformed;
    }
    
    // Also check if medication_data is directly in response (not nested in data)
    if (response.medication_data) {
      const medicationData = Array.isArray(response.medication_data) 
        ? response.medication_data 
        : (response.medication_data ? [response.medication_data] : []);
      
      const transformed = {
        ...response,
        data: {
          ...(response.data || {}),
          result: medicationData,
          medication_data: medicationData // Keep original for compatibility
        }
      };
      return transformed;
    }
    
    // Return response as-is if no transformation needed
    return response;
  }

  private connect(): Promise<void> {
    // A connect (including its async getWsAuthToken() step) is already in flight — reuse it so we
    // never open a second, orphaned socket.
    if (this.connectPromise) {
      return this.connectPromise;
    }
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.CONNECTING ||
        this.ws.readyState === WebSocket.OPEN)
    ) {
      return Promise.resolve();
    }
    this.connectPromise = this._doConnect().finally(() => {
      this.connectPromise = null;
    });
    return this.connectPromise;
  }

  private async _doConnect(): Promise<void> {
    try {
      // Don't create a new connection if one already exists and is connecting/open
      if (this.ws) {
        const state = this.ws.readyState;
        if (state === WebSocket.CONNECTING || state === WebSocket.OPEN) {
          return;
        }
      }

      // Clear any existing reconnect timer
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }

      if (shouldUseServerSessionAuth()) {
        const wsToken = await getWsAuthToken();
        const wsUrl = wsToken
          ? `${TEXT_API_URL}${TEXT_API_URL.includes("?") ? "&" : "?"}wsToken=${encodeURIComponent(wsToken)}`
          : TEXT_API_URL;
        this.ws = new WebSocket(wsUrl);
      } else {
        let wsUrl = TEXT_API_URL;
        // In JWT mode, pass stored sessionId as query param so backend recognizes returning connection
        const sessionId = getStoredOrchestrationSessionId();
        if (sessionId) {
          wsUrl += (TEXT_API_URL.includes("?") ? "&" : "?") + `jwt_session_id=${encodeURIComponent(sessionId)}`;
        }
        const jwtToken = getJwtTokenCookie();
        const protocols = jwtToken ? [`bearer.${jwtToken}`] : undefined;
        this.ws = protocols ? new WebSocket(wsUrl, protocols) : new WebSocket(wsUrl);
      }

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        resetOrchestrationSocketJwtBinding();
      };

      this.ws.onmessage = (event) => {
        try {
          const response = typeof event.data === 'string' 
            ? JSON.parse(event.data) 
            : event.data;
          
          if (response && typeof response === 'object') {
            // Persist server session id for draft resume (session store key with patientUid).
            if (response.type === "connected" && response.data?.session_id) {
              // Only update stored sessionId if this is NOT a resumed connection.
              // If backend recognized the prior sessionId and returned resumed_conversation,
              // keep using the old sessionId (don't overwrite with the new one).
              // This ensures the first message after reconnect uses the correct sessionId
              // to identify the returning conversation to the backend.
              if (!response.data?.resumed_conversation) {
                setStoredOrchestrationSessionId(String(response.data.session_id));
              }
              // Guard against clobbering a store that's already populated (e.g. a fast
              // reconnect where the page never actually lost its in-memory state).
              if (response.data?.resumed_conversation && getHeldConversations().length === 0) {
                // Validate that the resumed conversation is for the current patient.
                // If patient context changed since the session was created, the backend may
                // have stale data from a previous patient. Only apply resumed_conversation
                // if the patient_uid matches the current patient.
                const resumedPatientUid = response.data.resumed_conversation.patient_uid;
                const currentPatientId = getPatientId();

                // Apply resumed_conversation only if:
                // 1. Both patient UIDs are non-empty AND they match, OR
                // 2. Both are empty (backward compatibility for old snapshots without patient_uid)
                const shouldApply =
                  (currentPatientId && resumedPatientUid === currentPatientId) ||
                  (!currentPatientId && !resumedPatientUid);

                if (!shouldApply) {
                  // Patient context mismatch: either current patient differs from session,
                  // or current patient is unknown but session has stale patient data.
                  // Discard the resumed_conversation and proceed with empty state.
                  // The first user message will trigger backend patient change detection if needed.
                  if (currentPatientId || resumedPatientUid) {
                    console.warn(
                      '[WebSocket.connected] Resumed conversation discarded due to patient mismatch: ' +
                      `session patient_uid="${resumedPatientUid}" != current patient "${currentPatientId}"`
                    );
                  }
                } else {
                  applyResumedConversation(response.data.resumed_conversation);
                }
              }
              return;
            }
            // Check response type and handle accordingly
            // Handle pharmacy type response
            if (response.type === "pharmacy" && response.data) {
              if (this.preferredPharmacyCallback) {
                this.preferredPharmacyCallback(response.data);
              }
              // Also call intermediate message callback for pharmacy
              if (this.intermediateMessageCallback) {
                this.intermediateMessageCallback(response);
              }
              // Don't resolve pending request for pharmacy, continue to wait for result
              return;
            }

            // Handle error type response - show error in UI and reject pending request
            if (response.type === "error") {
              const errorMessage =
                response.message ||
                response.data?.message ||
                response.data?.error ||
                "An error occurred";
              if (this.pendingRequests.size > 0) {
                const firstRequestId = Array.from(this.pendingRequests.keys())[0];
                if (response.data?.code === "session_idle_timeout") {
                  if (this.retryAfterIdleTimeout(firstRequestId, errorMessage)) {
                    return;
                  }
                }
                const pendingRequest = this.pendingRequests.get(firstRequestId)!;
                clearTimeout(pendingRequest.timeout);
                this.pendingRequests.delete(firstRequestId);
                pendingRequest.reject(new Error(errorMessage));
              }
              if (this.intermediateMessageCallback) {
                this.intermediateMessageCallback(response);
              }
              return;
            }
            
            // Handle intermediate messages (status, token) - pass to callback but don't resolve
            if (response.type === "status" || response.type === "token") {
              if (this.intermediateMessageCallback) {
                this.intermediateMessageCallback(response);
              }
              // Don't resolve pending request for intermediate messages, continue to wait for result
              return;
            }
            
            // Handle result type response - this is the main response
            if (response.type === "result") {
              // Call intermediate callback with result message (includes medication_data)
              // This allows the component to see medication_data in real-time
              if (this.intermediateMessageCallback) {
                this.intermediateMessageCallback(response);
              }
              // Continue processing - don't return, let it resolve the promise
              
              // Ensure medication_data is properly extracted from result type response
              if (response.data) {
                if (response.data.medication_data) {
                  // Ensure medication_data is an array
                  if (!Array.isArray(response.data.medication_data)) {
                    response.data.medication_data = [response.data.medication_data];
                  }
                } else {
                  // If no medication_data, create empty array to prevent errors
                  if (!response.data.medication_data) {
                    response.data.medication_data = [];
                  }
                }
              }
            }
            
            // Transform response to match expected format
            const transformedResponse = this.transformResponse(response);
            
            // Double-check that result field exists after transformation
            if (transformedResponse.type === "result" && transformedResponse.data) {
              if (!transformedResponse.data.result && transformedResponse.data.medication_data) {
                transformedResponse.data.result = transformedResponse.data.medication_data;
              }
            }
            
            // Check if response has a requestId to match with pending request
            const requestId = transformedResponse.requestId || transformedResponse.id;
            
            if (requestId && this.pendingRequests.has(requestId)) {
              const pendingRequest = this.pendingRequests.get(requestId)!;
              clearTimeout(pendingRequest.timeout);
              this.pendingRequests.delete(requestId);
              
              // Ensure response has the correct structure: { data: { result: [...], ... } }
              const formattedResponse = ensureResultField(transformedResponse);
              pendingRequest.resolve(formattedResponse);
            } else {
              // If no requestId, try to resolve the first pending request
              // This handles cases where server doesn't send requestId
              if (this.pendingRequests.size > 0) {
                const firstRequestId = Array.from(this.pendingRequests.keys())[0];
                const pendingRequest = this.pendingRequests.get(firstRequestId)!;
                clearTimeout(pendingRequest.timeout);
                this.pendingRequests.delete(firstRequestId);
                
                // Ensure response has the correct structure: { data: { result: [...], ... } }
                const formattedResponse = ensureResultField(transformedResponse);
                pendingRequest.resolve(formattedResponse);
              } else {
                console.warn("⚠️ Received response but no pending requests found");
              }
            }
          } else {
            // Try to resolve first pending request with raw response
            if (this.pendingRequests.size > 0) {
              const firstRequestId = Array.from(this.pendingRequests.keys())[0];
              const pendingRequest = this.pendingRequests.get(firstRequestId)!;
              clearTimeout(pendingRequest.timeout);
              this.pendingRequests.delete(firstRequestId);
              
              // Try to parse and transform the raw data
              let parsedData;
              try {
                parsedData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                parsedData = this.transformResponse(parsedData);
              } catch (e) {
                parsedData = event.data;
              }
              
              // Ensure response has the correct structure
              const formattedResponse = ensureResultField(parsedData);
              pendingRequest.resolve(formattedResponse);
            }
          }
        } catch (error) {
          console.error("❌ Error parsing WebSocket message:", error);
          
          // Try to resolve first pending request with error
          if (this.pendingRequests.size > 0) {
            const firstRequestId = Array.from(this.pendingRequests.keys())[0];
            const pendingRequest = this.pendingRequests.get(firstRequestId)!;
            clearTimeout(pendingRequest.timeout);
            this.pendingRequests.delete(firstRequestId);
            pendingRequest.reject(new Error(`Failed to parse response: ${error}`));
          }
        }
      };

      this.ws.onerror = (error) => {
        console.error("❌ WebSocket error:", error);
      };

      this.ws.onclose = (event) => {

        // Reject all pending requests
        this.pendingRequests.forEach((request, requestId) => {
          clearTimeout(request.timeout);
          request.reject(new Error("WebSocket connection closed"));
        });
        this.pendingRequests.clear();

        // 1008 (policy violation) is how the orchestration rejects an expired/invalid session
        // (see _authenticate_websocket_on_connect). Retrying with a dead token just loops
        // "Connection error. Retrying…" forever — instead, log the user out to the login screen.
        if (event.code === 1008) {
          this.isManualClose = true; // stop any scheduled reconnect
          handleExpiredAuthSession();
          return;
        }

        // Attempt reconnection if not manually closed
        if (!this.isManualClose && !event.wasClean) {
          this.attemptReconnect();
        }
      };
    } catch (err: any) {
      console.error("❌ Failed to connect WebSocket:", err);
      
      // Reject all pending requests
      this.pendingRequests.forEach((request) => {
        clearTimeout(request.timeout);
        request.reject(new Error(`Failed to connect: ${err.message}`));
      });
      this.pendingRequests.clear();

      // Attempt reconnection
      if (!this.isManualClose) {
        this.attemptReconnect();
      }
    }
  }

  private attemptReconnect(): void {
    if (this.isManualClose) {
      return;
    }

    // Don't keep reconnecting once the session is genuinely expired. A staging/prod auth
    // rejection happens before accept and reaches the browser as code 1006 (not a readable
    // 1008), so we can't rely on the close code alone — check the session directly. This is
    // expiry-aware for both auth modes: in server-session mode it stays true while the 2h
    // session is alive (so a lapsed 5-min ws-token just refreshes on reconnect), and only
    // goes false when the underlying JWT/session is really dead → then log out to login.
    if (!hasClientAuthSession()) {
      this.isManualClose = true;
      handleExpiredAuthSession();
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // Don't permanently give up — the backend may be restarting or the server session may
      // have expired (it re-establishes via getWsAuthToken → /auth/session/exchange on the next
      // connect). Fall back to a slow background retry so the socket recovers on its own.
      console.warn(
        `WebSocket reconnect cap (${this.maxReconnectAttempts}) hit; scheduling slow background retry in ${this.slowReconnectDelay}ms.`
      );
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
      }
      this.reconnectTimer = setTimeout(() => {
        if (this.isManualClose) {
          return;
        }
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        if (this.ws) {
          try {
            this.ws.close();
          } catch (e) {
            // ignore
          }
          this.ws = null;
        }
        this.connect();
      }, this.slowReconnectDelay);
      return;
    }

    this.reconnectAttempts += 1;
    const delay = this.reconnectDelay;
    
    this.reconnectTimer = setTimeout(() => {
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 16000);
      
      // Close existing connection if it exists
      if (this.ws) {
        try {
          this.ws.close();
        } catch (e) {
          // Error closing WebSocket before reconnect
        }
        this.ws = null;
      }
      
      this.connect();
    }, delay);
  }

  private closeStaleSocket(): void {
    if (!this.ws) {
      return;
    }
    try {
      this.ws.close();
    } catch (e) {
      // Error closing stale WebSocket
    }
    this.ws = null;
  }

  private retryAfterIdleTimeout(requestId: string, errorMessage: string): boolean {
    const pendingRequest = this.pendingRequests.get(requestId);
    if (!pendingRequest || !pendingRequest.retryOnIdleTimeout) {
      return false;
    }

    clearTimeout(pendingRequest.timeout);
    this.pendingRequests.delete(requestId);
    setStoredOrchestrationSessionId(null);
    this.closeStaleSocket();

    const retryPayload = { ...pendingRequest.payload };
    delete retryPayload.sessionId;

    this.sendRequest(retryPayload, false)
      .then(pendingRequest.resolve)
      .catch((error) => {
        pendingRequest.reject(
          error instanceof Error ? error : new Error(errorMessage)
        );
      });
    return true;
  }

  public sendRequest(payload: any, retryOnIdleTimeout: boolean = true): Promise<any> {
    return new Promise((resolve, reject) => {
      // Generate unique request ID
      const requestId = `req_${Date.now()}_${++this.requestIdCounter}`;
      
      // Ensure WebSocket is connected
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        // A user-initiated send must always get a fresh retry budget: if an earlier burst
        // exhausted the reconnect cap (e.g. during a backend restart), re-arm it so this
        // action can recover — getWsAuthToken re-exchanges the JWT for a new session on connect.
        // Also clear any manual-close left by a prior logout so a re-login can reconnect without
        // a page refresh (the "session expired → re-login → only 'connected'" case).
        this.isManualClose = false;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        this.connect();

        // Allow enough time for a session re-exchange (POST /auth/session/exchange) plus the
        // WS handshake before failing the send.
        let checkConnection: ReturnType<typeof setInterval>;
        const connectionTimeout = setTimeout(() => {
          clearInterval(checkConnection);
          reject(new Error("WebSocket connection timeout"));
        }, 20000);

        checkConnection = setInterval(() => {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            clearTimeout(connectionTimeout);
            clearInterval(checkConnection);
            this.sendRequestInternal(
              payload,
              requestId,
              resolve,
              reject,
              retryOnIdleTimeout
            );
          }
          // Do NOT reject on a transient CLOSED state: a reconnect (with a freshly re-exchanged
          // session token) may be in progress. Wait for OPEN or the overall timeout above.
        }, 150);

        return;
      }

      this.sendRequestInternal(payload, requestId, resolve, reject, retryOnIdleTimeout);
    });
  }

  private sendRequestInternal(
    payload: any,
    requestId: string,
    resolve: (value: any) => void,
    reject: (error: any) => void,
    retryOnIdleTimeout: boolean = true
  ): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      reject(new Error("WebSocket is not connected"));
      return;
    }

    // Add requestId to payload
    const payloadWithId = {
      ...payload,
      requestId: requestId
    };

    // Set up timeout
    const timeout = setTimeout(() => {
      this.pendingRequests.delete(requestId);
      reject(new Error(`Request timeout after ${this.REQUEST_TIMEOUT}ms`));
    }, this.REQUEST_TIMEOUT);

    // Store pending request
    this.pendingRequests.set(requestId, {
      resolve,
      reject,
      timeout,
      requestId,
      payload,
      retryOnIdleTimeout,
    });

    // Send request
    try {
      this.ws.send(JSON.stringify(payloadWithId));
    } catch (error) {
      this.pendingRequests.delete(requestId);
      clearTimeout(timeout);
      devError("WebSocket send failed");
      reject(error);
    }
  }

  /**
   * Open the socket ahead of the first send (call on mount) so the first message doesn't pay the
   * full auth + handshake + backend cold-connect cost under the send timeout. Idempotent and safe
   * to call repeatedly — connect() no-ops if already CONNECTING/OPEN.
   */
  public ensureConnected(): void {
    // Clear a prior manual-close so a remounted assistant can reconnect.
    this.isManualClose = false;
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      this.connect();
    }
  }

  public close(): void {
    this.isManualClose = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Reject all pending requests
    this.pendingRequests.forEach((request) => {
      clearTimeout(request.timeout);
      request.reject(new Error("WebSocket manually closed"));
    });
    this.pendingRequests.clear();

    if (this.ws) {
      try {
        this.ws.close();
      } catch (e) {
        // Error closing WebSocket
      }
      this.ws = null;
    }
  }

  public isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Fire-and-forget user feedback (thumbs up/down) for a turn, attached to its
   * Langfuse trace id. Best-effort: persists as a `user_feedback` score server
   * side; we don't await an ack (feedback must never block the UI).
   */
  public sendFeedback(traceId: string, value: "up" | "down"): void {
    if (!traceId) return;
    this.ensureConnected();
    try {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: "feedback", traceId, value }));
      }
    } catch (e) {
      // Non-fatal: feedback is best-effort.
    }
  }
}

// Create singleton instance
let wsManagerInstance: TextWebSocketManager | null = null;

export const getTextWebSocketManager = (): TextWebSocketManager => {
  if (!wsManagerInstance) {
    wsManagerInstance = new TextWebSocketManager();
  }
  return wsManagerInstance;
};

// Export function to send request via WebSocket
export const sendTextWebSocketRequest = (payload: any): Promise<any> => {
  const manager = getTextWebSocketManager();
  return manager.sendRequest(payload);
};

